import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ApiClient } from '../api/client';
import type { HostMaintenanceCampaign, ManagedHost } from '../types';
import { reportErrorLocal } from '../utils/errors';
import ErrorBanner from './ErrorBanner';
import HostMaintenanceInventory, { maintenanceInventoryGroup, maintenanceInventoryLabel, maintenanceSelectableHostIds } from './HostMaintenanceInventory';
import type { MaintenanceInventoryView } from './HostMaintenanceInventory';

type Props = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  canConfigureTopology: boolean;
  canViewNodes: boolean;
  canOperate: boolean;
};

type ManagedHostDraft = {
  hostRef: string;
  displayName: string;
  maintenanceEnabled: boolean;
  parentHostId: string;
  failureDomain: string;
  controlPlane: boolean;
  maintenanceOrder: number;
};

function managedHostDraft(host: ManagedHost): ManagedHostDraft {
  return {
    hostRef: host.host_ref,
    displayName: host.display_name ?? '',
    maintenanceEnabled: host.maintenance_enabled,
    parentHostId: host.parent_host_id ?? '',
    failureDomain: host.failure_domain ?? '',
    controlPlane: host.control_plane,
    maintenanceOrder: host.maintenance_order
  };
}

function formatTimestamp(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-';
}

function maintenanceStatusClass(status?: string | null) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'succeeded') return 'badge badge--success';
  if (normalized === 'failed' || normalized === 'canceled') return 'badge badge--error';
  if (['queued', 'running', 'draining', 'updating', 'awaiting_reconnect', 'verifying', 'canceling'].includes(normalized)) {
    return 'badge badge--processing';
  }
  return 'badge';
}

export default function HostMaintenancePanel({ apiClient, setError, canConfigureTopology, canViewNodes, canOperate }: Props) {
  const [inventoryView, setInventoryView] = useState<MaintenanceInventoryView>('current');
  const inventoryViewRef = useRef<MaintenanceInventoryView>('current');
  const [hosts, setHosts] = useState<ManagedHost[]>([]);
  const [campaigns, setCampaigns] = useState<HostMaintenanceCampaign[]>([]);
  const [selectedHostIds, setSelectedHostIds] = useState<Set<string>>(new Set());
  const [drafts, setDrafts] = useState<Record<string, ManagedHostDraft>>({});
  const [delegateEmails, setDelegateEmails] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [importingTopology, setImportingTopology] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const requestVersion = useRef(0);
  const dirtyHosts = useRef(new Set<string>());

  const load = useCallback(async (foreground = false) => {
    const version = ++requestVersion.current;
    if (foreground) setLoading(true);
    try {
      const [hostRows, campaignRows] = await Promise.all([
        apiClient.get<ManagedHost[]>('/api/maintenance/hosts'),
        apiClient.get<HostMaintenanceCampaign[]>('/api/maintenance/campaigns')
      ]);
      if (version !== requestVersion.current) return;
      const nextHosts = hostRows ?? [];
      setHosts(nextHosts);
      setCampaigns(campaignRows ?? []);
      setDrafts((current) => Object.fromEntries(nextHosts.map((host) => [
        host.id,
        dirtyHosts.current.has(host.id) && current[host.id]
          ? current[host.id]
          : managedHostDraft(host)
      ])));
      const reserved = new Set((campaignRows ?? [])
        .filter((campaign) => ['planned', 'running', 'canceling'].includes(campaign.status.toLowerCase()))
        .flatMap((campaign) => campaign.tasks.map((task) => task.host_id)));
      const selectable = new Set(maintenanceSelectableHostIds(
        nextHosts, inventoryViewRef.current, reserved
      ));
      setSelectedHostIds((current) =>
        new Set([...current].filter((hostId) => selectable.has(hostId)))
      );
      setLocalError(null);
    } catch (error) {
      if (version !== requestVersion.current) return;
      reportErrorLocal('Failed to load host maintenance', error, setError, setLocalError);
    } finally {
      if (foreground && version === requestVersion.current) setLoading(false);
    }
  }, [apiClient, setError]);

  useEffect(() => {
    void load(true);
  }, [load]);

  const activeCampaigns = campaigns.filter((campaign) =>
    ['planned', 'running', 'canceling'].includes(campaign.status.toLowerCase())
  );
  const reservedHostIds = new Set(
    activeCampaigns.flatMap((campaign) => campaign.tasks.map((task) => task.host_id))
  );
  const recentCampaigns = campaigns
    .filter((campaign) => !activeCampaigns.some((active) => active.id === campaign.id))
    .slice(0, 5);

  useEffect(() => {
    const interval = window.setInterval(
      () => void load(),
      activeCampaigns.length > 0 ? 5000 : 30000
    );
    return () => window.clearInterval(interval);
  }, [activeCampaigns.length, load]);

  const updateDraft = (host: ManagedHost, patch: Partial<ManagedHostDraft>) => {
    dirtyHosts.current.add(host.id);
    setDrafts((current) => ({
      ...current,
      [host.id]: { ...(current[host.id] ?? managedHostDraft(host)), ...patch }
    }));
  };

  const saveHost = async (host: ManagedHost) => {
    const draft = drafts[host.id] ?? managedHostDraft(host);
    setSavingKey(`host:${host.id}`);
    setStatus(null);
    try {
      await apiClient.patch(`/api/maintenance/hosts/${encodeURIComponent(host.id)}`, {
        host_ref: draft.hostRef,
        display_name: draft.displayName.trim() || null,
        maintenance_enabled: draft.maintenanceEnabled,
        ...(canConfigureTopology ? {
          parent_host_id: draft.parentHostId || null,
          failure_domain: draft.failureDomain.trim() || null,
          control_plane: draft.controlPlane,
          maintenance_order: draft.maintenanceOrder
        } : {})
      });
      dirtyHosts.current.delete(host.id);
      setStatus(`${draft.displayName.trim() || draft.hostRef} settings saved.`);
      await load();
    } catch (error) {
      reportErrorLocal('Failed to update managed host', error, setError, setLocalError);
    } finally {
      setSavingKey(null);
    }
  };

  const startMaintenance = async (hostIds: string[]) => {
    if (starting || hostIds.length === 0) return;
    const selected = hosts.filter((host) => hostIds.includes(host.id));
    if (selected.length !== hostIds.length || selected.some((host) => !host.ready)) {
      setLocalError('Every selected host must be enabled, online, unambiguous, and maintenance-capable.');
      return;
    }
    if (selected.some((host) => reservedHostIds.has(host.id))) {
      setLocalError('One or more selected hosts already belong to an active maintenance campaign.');
      return;
    }
    const labels = selected.map((host) => host.display_name || host.host_ref).join(', ');
    if (!window.confirm(`Install system updates on ${labels}? Hosts may reboot after their work drains.`)) return;
    setStarting(true);
    setStatus('Starting maintenance campaign...');
    try {
      const started = await apiClient.post<HostMaintenanceCampaign>('/api/maintenance/start', {
        confirm: true,
        host_ids: hostIds,
        reboot_if_required: true
      });
      setCampaigns((current) => [started, ...current.filter((row) => row.id !== started.id)]);
      setSelectedHostIds(new Set());
      setStatus(`Maintenance started for ${hostIds.length} host${hostIds.length === 1 ? '' : 's'}.`);
    } catch (error) {
      reportErrorLocal('Failed to start fleet maintenance', error, setError, setLocalError);
    } finally {
      setStarting(false);
      await load();
    }
  };

  const cancelCampaign = async (campaign: HostMaintenanceCampaign) => {
    if (!window.confirm('Cancel remaining work in this maintenance campaign?')) return;
    try {
      const updated = await apiClient.post<HostMaintenanceCampaign>(
        `/api/maintenance/campaigns/${encodeURIComponent(campaign.id)}/cancel`,
        {}
      );
      setCampaigns((current) => current.map((row) => row.id === updated.id ? updated : row));
      setStatus(
        updated.status === 'canceling'
          ? 'Cancellation requested; the in-flight host will finish safely.'
          : 'Maintenance campaign canceled.'
      );
    } catch (error) {
      reportErrorLocal('Failed to cancel maintenance campaign', error, setError, setLocalError);
    }
  };

  const importTopology = async (file: File | null) => {
    if (!file) return;
    setImportingTopology(true);
    setStatus(null);
    try {
      const result = await apiClient.post<{ updated_hosts: number }>(
        '/api/maintenance/topology/import',
        { content: await file.text() }
      );
      dirtyHosts.current.clear();
      setStatus(`Fleet topology applied to ${result.updated_hosts} host${result.updated_hosts === 1 ? '' : 's'}.`);
      await load();
    } catch (error) {
      reportErrorLocal('Failed to import fleet topology', error, setError, setLocalError);
    } finally {
      setImportingTopology(false);
    }
  };

  const addDelegate = async (host: ManagedHost) => {
    const email = delegateEmails[host.id]?.trim() ?? '';
    if (!email) return;
    setSavingKey(`delegate:${host.id}`);
    try {
      await apiClient.post(
        `/api/maintenance/hosts/${encodeURIComponent(host.id)}/operators`,
        { email }
      );
      setDelegateEmails((current) => ({ ...current, [host.id]: '' }));
      setStatus(`Maintenance access granted to ${email}.`);
      await load();
    } catch (error) {
      reportErrorLocal('Failed to add host delegate', error, setError, setLocalError);
    } finally {
      setSavingKey(null);
    }
  };

  const removeDelegate = async (host: ManagedHost, userId: string, label: string) => {
    if (!window.confirm(`Remove ${label} from this host?`)) return;
    setSavingKey(`delegate:${host.id}`);
    try {
      await apiClient.del(
        `/api/maintenance/hosts/${encodeURIComponent(host.id)}/operators/${encodeURIComponent(userId)}`
      );
      setStatus(`Maintenance access removed from ${label}.`);
      await load();
    } catch (error) {
      reportErrorLocal('Failed to remove host delegate', error, setError, setLocalError);
    } finally {
      setSavingKey(null);
    }
  };

  const visibleHosts = hosts.filter((host) =>
    inventoryView === 'all' || maintenanceInventoryGroup(host) === inventoryView
  );
  const readyHostIds = maintenanceSelectableHostIds(hosts, inventoryView, reservedHostIds);
  // Re-evaluate at render time: polling can hide or reserve a previously selected host.
  const actionableSelectedHostIds = readyHostIds.filter((id) => selectedHostIds.has(id));
  const toggleSelection = (hostId: string) => {
    setSelectedHostIds((current) => {
      const next = new Set(current);
      if (next.has(hostId)) next.delete(hostId);
      else next.add(hostId);
      return next;
    });
  };

  return (
    <div className="card full" id="host-maintenance">
      <div className="card-header">
        <div>
          <h3>Host Maintenance</h3>
          <p className="status">Serial system updates with topology-aware reboot ordering.</p>
        </div>
        <div className="row row--compact">
          {canConfigureTopology && (
            <label className="button secondary compact">
              {importingTopology ? 'Importing...' : 'Import YAML'}
              <input
                className="visually-hidden"
                type="file"
                accept=".yaml,.yml,text/yaml,application/yaml"
                disabled={importingTopology || activeCampaigns.length > 0}
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  event.target.value = '';
                  void importTopology(file);
                }}
              />
            </label>
          )}
          <button
            type="button"
            className="secondary compact"
            disabled={readyHostIds.length === 0}
            onClick={() => setSelectedHostIds(new Set(readyHostIds))}
          >
            Select ready
          </button>
          <button
            type="button"
            className="compact"
            disabled={actionableSelectedHostIds.length === 0 || starting}
            onClick={() => void startMaintenance(actionableSelectedHostIds)}
          >
            {starting ? 'Starting...' : 'Patch selected'}
          </button>
        </div>
      </div>

      <HostMaintenanceInventory
        hosts={hosts}
        view={inventoryView}
        canViewNodes={canViewNodes}
        canOperate={canOperate}
        onChange={(view) => {
          inventoryViewRef.current = view;
          setInventoryView(view);
          setSelectedHostIds(new Set());
        }}
      />

      {localError && <ErrorBanner message={localError} onDismiss={() => setLocalError(null)} />}
      {status && <p className="status status-text--ok">{status}</p>}

      {activeCampaigns.map((campaign) => (
        <div className="maintenance-campaign" key={campaign.id}>
          <div className="row row--spread">
            <div className="stack stack--compact">
              <strong>Campaign {campaign.id.slice(0, 8)}</strong>
              <span className="hint">
                {campaign.tasks.length} hosts · started{' '}
                {formatTimestamp(campaign.started_at || campaign.created_at)}
              </span>
            </div>
            <div className="row row--compact">
              <span className={maintenanceStatusClass(campaign.status)}>{campaign.status}</span>
              <button
                type="button"
                className="danger compact"
                disabled={campaign.status === 'canceling'}
                onClick={() => void cancelCampaign(campaign)}
              >
                {campaign.status === 'canceling' ? 'Canceling...' : 'Cancel'}
              </button>
            </div>
          </div>
          <ol className="maintenance-task-list">
            {campaign.tasks.map((task) => (
              <li key={task.id}>
                <span className="maintenance-task-list__host">
                  {task.sequence}. {task.display_name || task.host_ref}
                </span>
                <span className={maintenanceStatusClass(task.status)}>{task.status}</span>
                <span className="hint">{task.phase.replaceAll('_', ' ')}</span>
                {task.reboot_required && <span className="badge">reboot</span>}
                {task.error && <span className="warning-text">{task.error}</span>}
              </li>
            ))}
          </ol>
        </div>
      ))}

      {recentCampaigns.length > 0 && (
        <details className="maintenance-history">
          <summary>Recent maintenance</summary>
          <ol className="maintenance-history__list">
            {recentCampaigns.map((campaign) => (
              <li key={campaign.id}>
                <span className="mono">{campaign.id.slice(0, 8)}</span>
                <span>{campaign.tasks.length} host{campaign.tasks.length === 1 ? '' : 's'}</span>
                <span className={maintenanceStatusClass(campaign.status)}>{campaign.status}</span>
                <span className="hint">
                  {formatTimestamp(campaign.finished_at || campaign.updated_at)}
                </span>
              </li>
            ))}
          </ol>
        </details>
      )}

      <div className="table-scroll responsive-table-scroll">
        <table className="responsive-table managed-host-table">
          <thead>
            <tr>
              <th aria-label="Select" />
              <th>Host</th>
              <th>Readiness</th>
              <th>Topology</th>
              <th>Activity</th>
              <th>Settings</th>
            </tr>
          </thead>
          <tbody>
            {visibleHosts.map((host) => {
              const draft = drafts[host.id] ?? managedHostDraft(host);
              const owner = host.owner?.name || host.owner?.email || (
                host.owner_user_id ? `User ${host.owner_user_id.slice(0, 8)}` : 'Platform'
              );
              return (
                <tr key={host.id}>
                  <td data-label="Select">
                    <input
                      type="checkbox"
                      aria-label={`Select ${host.display_name || host.host_ref}`}
                      checked={actionableSelectedHostIds.includes(host.id)}
                      disabled={!host.ready || reservedHostIds.has(host.id)}
                      onChange={() => toggleSelection(host.id)}
                    />
                  </td>
                  <td data-label="Host" className="executor-table__identity">
                    <div className="stack stack--compact">
                      <strong>{host.display_name || host.host_ref}</strong>
                      <span className="mono">{host.host_ref}</span>
                      <span className="badge">{maintenanceInventoryLabel(host)}</span>
                      <span className="hint mono">Host ID: {host.id}</span>
                      <span className="hint mono">Enrollment token ID: {host.worker_token_id}</span>
                      <span className="hint">{host.detected_hostname || 'hostname not reported'} · {owner}</span>
                      <span className="hint">{host.management_class.replaceAll('_', ' ')}</span>
                    </div>
                  </td>
                  <td data-label="Readiness">
                    <div className="stack stack--compact">
                      <span className={host.ready ? 'badge badge--success' : 'badge badge--error'}>
                        {host.ready ? 'ready' : 'blocked'}
                      </span>
                      <span className="hint">
                        {host.online_executor_count}/{host.executor_count} online · {host.capable_executor_count} capable
                      </span>
                      {!host.maintenance_enabled && <span className="warning-text">maintenance disabled</span>}
                      {host.hostname_ambiguous && <span className="warning-text">multiple hostnames on one token</span>}
                    </div>
                  </td>
                  <td data-label="Topology">
                    <div className="stack stack--compact">
                      <span>{host.parent_host_ref ? `VM on ${host.parent_host_ref}` : 'Physical / independent'}</span>
                      {host.failure_domain && <span className="hint">domain {host.failure_domain}</span>}
                      {host.control_plane && <span className="badge">control plane · last</span>}
                      <span className="hint">order {host.maintenance_order}</span>
                    </div>
                  </td>
                  <td data-label="Activity">
                    <div className="stack stack--compact">
                      <span>{host.active_workload_count} workloads</span>
                      <span>{host.active_terminal_count} terminals</span>
                    </div>
                  </td>
                  <td data-label="Settings" className="responsive-table__actions">
                    {host.actor_can_delegate ? <details className="managed-host-settings">
                      <summary>Configure</summary>
                      <div className="managed-host-settings__grid">
                        <label>
                          Reference
                          <input value={draft.hostRef} onChange={(event) => updateDraft(host, { hostRef: event.target.value })} />
                        </label>
                        <label>
                          Display name
                          <input value={draft.displayName} onChange={(event) => updateDraft(host, { displayName: event.target.value })} />
                        </label>
                        <label className="executor-toggle-row">
                          <input
                            type="checkbox"
                            checked={draft.maintenanceEnabled}
                            onChange={(event) => updateDraft(host, { maintenanceEnabled: event.target.checked })}
                          />
                          Enable OS maintenance
                        </label>
                        {canConfigureTopology && (
                          <>
                            <label>
                              Parent host
                              <select value={draft.parentHostId} onChange={(event) => updateDraft(host, { parentHostId: event.target.value })}>
                                <option value="">None</option>
                                {hosts.filter((candidate) => candidate.id !== host.id).map((candidate) => (
                                  <option key={candidate.id} value={candidate.id}>
                                    {candidate.display_name || candidate.host_ref}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              Failure domain
                              <input value={draft.failureDomain} onChange={(event) => updateDraft(host, { failureDomain: event.target.value })} />
                            </label>
                            <label>
                              Order
                              <input
                                type="number"
                                value={draft.maintenanceOrder}
                                onChange={(event) => updateDraft(host, { maintenanceOrder: Number(event.target.value) || 0 })}
                              />
                            </label>
                            <label className="executor-toggle-row">
                              <input
                                type="checkbox"
                                checked={draft.controlPlane}
                                onChange={(event) => updateDraft(host, { controlPlane: event.target.checked })}
                              />
                              Control plane (last)
                            </label>
                          </>
                        )}
                        {host.actor_can_delegate && (
                          <div className="managed-host-delegates">
                            <span className="hint">Maintenance delegates</span>
                            {host.operators.map((operator) => {
                              const label = operator.name || operator.email || operator.user_id;
                              return (
                                <div className="row row--spread" key={operator.user_id}>
                                  <span>{label}</span>
                                  <button
                                    type="button"
                                    className="danger compact"
                                    disabled={savingKey === `delegate:${host.id}`}
                                    onClick={() => void removeDelegate(host, operator.user_id, label)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              );
                            })}
                            <div className="row row--compact">
                              <input
                                type="email"
                                aria-label={`Delegate email for ${host.display_name || host.host_ref}`}
                                placeholder="member@example.com"
                                value={delegateEmails[host.id] ?? ''}
                                onChange={(event) => setDelegateEmails((current) => ({
                                  ...current,
                                  [host.id]: event.target.value
                                }))}
                              />
                              <button
                                type="button"
                                className="secondary compact"
                                disabled={!delegateEmails[host.id]?.trim() || savingKey === `delegate:${host.id}`}
                                onClick={() => void addDelegate(host)}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          className="compact"
                          disabled={savingKey === `host:${host.id}` || reservedHostIds.has(host.id)}
                          onClick={() => void saveHost(host)}
                        >
                          {savingKey === `host:${host.id}` ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </details> : <span className="hint">Owner managed</span>}
                  </td>
                </tr>
              );
            })}
            {!loading && visibleHosts.length === 0 && (
              <tr className="responsive-table__empty">
                <td colSpan={6}><p className="status">{hosts.length === 0 ? 'No managed hosts are registered yet.' : 'No hosts in this view. Check all registrations for historical or unknown entries.'}</p></td>
              </tr>
            )}
            {loading && hosts.length === 0 && (
              <tr className="responsive-table__empty">
                <td colSpan={6}><p className="status">Loading managed hosts...</p></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
